import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import capitalizeFirst from "../../utils/capitalizeFirst";
import { formatSocialTimestamp } from "../../utils/dateFormatter";
import { renderMentionText } from "../../utils/renderMentionText";
import PostComments from "./PostComments";
import ConfirmModal from "../common/ConfirmModal";
import LikesModal from "../common/LikesModal";
import EditPost from "./EditPost";
import ReportModal from "../moderation/ReportModal";
import SaveCollectionModal from "./SaveCollectionModal";
import SharePostModal from "./SharePostModal";
import {
  FaHeart,
  FaRegHeart,
  FaComment,
  FaShare,
  FaExternalLinkAlt,
  FaTimes,
  FaEdit,
  FaBookmark,
  FaRegBookmark,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisV,
  FaFlag,
  FaBell,
  FaEyeSlash,
  FaFire,
  FaQuestionCircle,
  FaBolt,
  FaStar,
  FaGlobe,
  FaUserFriends,
  FaLock,
} from "react-icons/fa";
import styles from "../../styles/components/profile/PostCard.module.css";
import FeedMatchFusedLine from "../feed/FeedMatchFusedLine";

const POST_VISIBILITY_DISPLAY = {
  public: { title: "Public post", Icon: FaGlobe },
  followers: { title: "Followers only", Icon: FaUserFriends },
  private: { title: "Private post", Icon: FaLock },
};

const PostCard = ({
  post,
  onDelete,
  onLikeUpdate,
  onPostUpdated,
  onVoteUpdate,
  onSaveUpdate,
  onCommentUpdate,
  isGridView = true,
  className,
  defaultShowComments = false,
  commentAction = "toggle",
  onCommentNavigate,
  scrollToComments = false,
  disableCardClick = false,
  onEdit,
  commentsRef,
  activeDecision = null,
  shopperCategoryKeys = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useUserAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [comparisonImageIndices, setComparisonImageIndices] = useState({
    A: 0,
    B: 0,
  });
  const [isLiked, setIsLiked] = useState(post.isLiked || false);
  const [likeCount, setLikeCount] = useState(
    post.likeCount || post.likes?.length || 0,
  );
  const [commentCount, setCommentCount] = useState(
    Number(post.commentCount ?? post.comments?.length ?? 0),
  );

  const updateCommentCount = (next) => {
    setCommentCount(next);
    onCommentUpdate?.(post._id, next);
  };

  const [comparisonStats, setComparisonStats] = useState(
    post.comparisonStats || {
      votesA: 0,
      votesB: 0,
      totalVotes: 0,
      percentA: 0,
      percentB: 0,
      userVote: null,
    },
  );
  const [showComments, setShowComments] = useState(defaultShowComments);
  const [hasLoadedComments, setHasLoadedComments] = useState(false);
  const [isSaved, setIsSaved] = useState(post.isSaved || false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isFollowingPost, setIsFollowingPost] = useState(
    post.isFollowingPost || false,
  );
  const [saving, setSaving] = useState(false);
  const [followingPost, setFollowingPost] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [backdropColor, setBackdropColor] = useState(null);
  const colorCache = React.useRef({});

  // Debounced view tracking using IntersectionObserver
  const viewTrackedRef = React.useRef(false);
  const viewTimerRef = React.useRef(null);

  useEffect(() => {
    if (!post._id || viewTrackedRef.current) return undefined;

    const cardElement = document.getElementById(`post-card-${post._id}`);
    if (!cardElement) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !viewTrackedRef.current) {
          // Start 1-second timer to ensure meaningful view
          viewTimerRef.current = setTimeout(async () => {
            if (!viewTrackedRef.current) {
              viewTrackedRef.current = true;
              try {
                await api.post(`/posts/${post._id}/view`);
              } catch (err) {
                console.debug("View tracking failed:", err);
              }
            }
          }, 1000);
        } else if (!entry.isIntersecting && viewTimerRef.current) {
          // User scrolled away before threshold — cancel timer
          clearTimeout(viewTimerRef.current);
          viewTimerRef.current = null;
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(cardElement);

    return () => {
      observer.disconnect();
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
      }
    };
  }, [post._id]);

  // Reset when post ID changes (new post)
  useEffect(() => {
    setCommentCount(post.commentCount || post.comments?.length || 0);
    setHasLoadedComments(false);
    setShowComments(defaultShowComments);
    setIsLiked(post.isLiked || false);
    setLikeCount(post.likeCount || post.likes?.length || 0);
    setIsFollowingPost(post.isFollowingPost || false);
    setComparisonStats(
      post.comparisonStats || {
        votesA: 0,
        votesB: 0,
        totalVotes: 0,
        percentA: 0,
        percentB: 0,
        userVote: null,
      },
    );
    setCurrentImageIndex(0);
  }, [
    post._id,
    defaultShowComments,
    post.isLiked,
    post.likeCount,
    post.commentCount,
    post.comments?.length,
    post.likes?.length,
    post.isFollowingPost,
    post.comparisonStats,
  ]); // Reset when post data changes
  const [liking, setLiking] = useState(false);
  const [voting, setVoting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const postImageRef = React.useRef(null);
  const captionRef = React.useRef(null);
  const menuRef = React.useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Memoize the read more calculation to prevent unnecessary re-renders
  const shouldShowReadMore = React.useMemo(() => {
    return post.caption && isGridView && post.caption.length > 140;
  }, [post.caption, isGridView]);

  // Update showReadMore state when the memoized value changes
  React.useEffect(() => {
    setShowReadMore(shouldShowReadMore);
  }, [shouldShowReadMore]);

  const products = React.useMemo(() => post.products || [], [post.products]);

  const postTitle = React.useMemo(() => {
    if (
      (post?.postType === "comparison" || post?.postType === "battle") &&
      post?.comparison
    ) {
      const a = post.comparison?.optionA?.title;
      const b = post.comparison?.optionB?.title;
      const aTitle = a ? capitalizeFirst(String(a)) : null;
      const bTitle = b ? capitalizeFirst(String(b)) : null;
      if (aTitle && bTitle) return `${aTitle} vs ${bTitle}`;
      if (aTitle) return aTitle;
      if (bTitle) return bTitle;
    }

    if (Array.isArray(products) && products.length > 0) {
      const names = products
        .map((p) => (typeof p === "string" ? null : p?.name))
        .filter(Boolean)
        .map((name) => capitalizeFirst(String(name)));

      if (names.length > 0) return names.join(", ");
    }

    // Fallback to manually entered title (from "Name of product" / "Give your post a title...")
    if (post?.title && post.title.trim()) {
      return capitalizeFirst(post.title.trim());
    }

    return null;
  }, [post?.postType, post?.comparison, post?.title, products]);

  const getProductId = (product) => {
    if (!product) return null;
    if (typeof product === "string") return product;
    return product._id || product.id || null;
  };

  const hasProductImages =
    post.productImages && Object.keys(post.productImages).length > 0;
  const fallbackImages = post.images || [];

  const getImagesForProduct = (product) => {
    const productId = getProductId(product);
    if (
      hasProductImages &&
      productId &&
      Array.isArray(post.productImages[productId]) &&
      post.productImages[productId].length > 0
    ) {
      return post.productImages[productId];
    }
    return fallbackImages;
  };

  const productImageEntries = products.reduce((acc, product, index) => {
    const images = getImagesForProduct(product) || [];
    images.forEach((image, imageIndex) => {
      acc.push({
        productIndex: index,
        imageIndex,
        image,
        productId: getProductId(product),
      });
    });
    return acc;
  }, []);

  if (productImageEntries.length === 0 && fallbackImages.length > 0) {
    fallbackImages.forEach((image, imageIndex) => {
      productImageEntries.push({
        productIndex: 0,
        imageIndex,
        image,
        productId: getProductId(products[0]),
      });
    });
  }

  const safeImageIndex =
    productImageEntries.length > 0
      ? Math.min(currentImageIndex, productImageEntries.length - 1)
      : 0;
  const currentEntry = productImageEntries[safeImageIndex] || null;
  const currentProductIndex = currentEntry?.productIndex || 0;
  const currentProduct = products[currentProductIndex] || products[0] || null;
  const currentImageUrl = productImageEntries[safeImageIndex]?.image
    ? getImageUrl(productImageEntries[safeImageIndex].image)
    : "";
  const containerBg = backdropColor
    ? `rgb(${backdropColor.r}, ${backdropColor.g}, ${backdropColor.b})`
    : undefined;

  const isOwnPost =
    currentUser &&
    post.user &&
    (currentUser.id === post.user._id || currentUser._id === post.user._id);
  const visibilityDisplay =
    POST_VISIBILITY_DISPLAY[post.visibility] || POST_VISIBILITY_DISPLAY.public;
  const VisibilityIcon = visibilityDisplay.Icon;
  const isComparisonPost =
    (post.postType === "comparison" || post.postType === "battle") &&
    post.comparison;
  const isBattlePost = post.postType === "battle";

  useEffect(() => {
    setComparisonImageIndices({ A: 0, B: 0 });
  }, [post._id]);

  const getComparisonOptionProduct = (option, optionKey) => {
    if (!option) return null;

    const optionProductId = option.product
      ? getProductId(option.product)
      : option.productId
        ? String(option.productId)
        : null;

    if (optionProductId) {
      const matched = products.find(
        (p) => String(getProductId(p)) === String(optionProductId),
      );
      if (matched) return matched;
    }

    if (products.length >= 2) {
      return optionKey === "A" ? products[0] : products[1];
    }
    if (products.length === 1) return products[0];
    return null;
  };

  const getComparisonOptionImages = (option, optionKey) => {
    const product = getComparisonOptionProduct(option, optionKey);
    if (product) {
      const images = getImagesForProduct(product) || [];
      if (images.length > 0) return images;
    }

    const optionProductId = option?.product
      ? getProductId(option.product)
      : option?.productId || null;
    if (
      optionProductId &&
      hasProductImages &&
      Array.isArray(post.productImages[optionProductId]) &&
      post.productImages[optionProductId].length > 0
    ) {
      return post.productImages[optionProductId];
    }

    if (option?.image) return [option.image];
    return [];
  };

  const stepComparisonImage = (optionKey, direction) => {
    const option =
      optionKey === "A" ? post.comparison?.optionA : post.comparison?.optionB;
    const images = getComparisonOptionImages(option, optionKey);
    if (images.length <= 1) return;

    setComparisonImageIndices((prev) => {
      const current = prev[optionKey] || 0;
      const next =
        direction === "next"
          ? (current + 1) % images.length
          : (current - 1 + images.length) % images.length;
      return { ...prev, [optionKey]: next };
    });
  };

  const getComparisonOptionImage = (option, optionKey) => {
    const images = getComparisonOptionImages(option, optionKey);
    const index = comparisonImageIndices[optionKey] || 0;
    const safeIndex =
      images.length > 0 ? Math.min(index, images.length - 1) : 0;
    return images[safeIndex] || "";
  };

  const renderComparisonOption = (optionKey) => {
    const option =
      optionKey === "A" ? post.comparison?.optionA : post.comparison?.optionB;
    const title = option?.title || `Option ${optionKey}`;
    const votes =
      optionKey === "A" ? comparisonStats.votesA : comparisonStats.votesB;
    const percent =
      optionKey === "A" ? comparisonStats.percentA : comparisonStats.percentB;
    const otherPercent =
      optionKey === "A" ? comparisonStats.percentB : comparisonStats.percentA;
    const isSelected = comparisonStats.userVote === optionKey;
    const hasVotes = (comparisonStats.totalVotes || 0) > 0;
    const isLeading = hasVotes && percent > otherPercent;
    const optionImages = getComparisonOptionImages(option, optionKey);
    const imageIndex = comparisonImageIndices[optionKey] || 0;
    const safeImageIndex =
      optionImages.length > 0
        ? Math.min(imageIndex, optionImages.length - 1)
        : 0;
    const imagePath = getComparisonOptionImage(option, optionKey);
    const hasMultipleImages = optionImages.length > 1;
    const optionProduct = getComparisonOptionProduct(option, optionKey);
    const productId =
      getProductId(optionProduct) ||
      (option?.product ? getProductId(option.product) : null);

    return (
      <div
        className={`${styles.comparisonOptionCard} ${
          isSelected ? styles.comparisonOptionSelected : ""
        } ${isLeading ? styles.comparisonOptionLeading : ""}`}
      >
        <div className={styles.comparisonOptionImageWrap}>
          <button
            type="button"
            className={styles.comparisonOptionImageVote}
            onClick={(e) => {
              e.stopPropagation();
              handleVote(optionKey);
            }}
            disabled={voting}
            aria-pressed={isSelected}
            aria-label={`Vote for ${title}`}
          >
            {imagePath ? (
              <img
                src={getImageUrl(imagePath)}
                alt=""
                className={styles.comparisonOptionImage}
                loading="lazy"
              />
            ) : (
              <div className={styles.comparisonOptionImageFallback} aria-hidden>
                {optionKey}
              </div>
            )}
          </button>
          {hasMultipleImages && (
            <>
              <button
                type="button"
                className={`preserveTransform ${styles.comparisonOptionNav} ${styles.comparisonOptionNavLeft}`}
                onClick={(e) => {
                  e.stopPropagation();
                  stepComparisonImage(optionKey, "prev");
                }}
                aria-label={`Previous photo for ${title}`}
              >
                <FaChevronLeft size={14} />
              </button>
              <button
                type="button"
                className={`preserveTransform ${styles.comparisonOptionNav} ${styles.comparisonOptionNavRight}`}
                onClick={(e) => {
                  e.stopPropagation();
                  stepComparisonImage(optionKey, "next");
                }}
                aria-label={`Next photo for ${title}`}
              >
                <FaChevronRight size={14} />
              </button>
              <div className={styles.comparisonOptionPagination} aria-hidden>
                {optionImages.map((_, index) => (
                  <span
                    key={index}
                    className={`${styles.comparisonOptionDot} ${
                      index === safeImageIndex
                        ? styles.comparisonOptionDotActive
                        : ""
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          {isSelected && (
            <span className={styles.comparisonOptionCheck} aria-hidden>
              ✓
            </span>
          )}
        </div>
        <button
          type="button"
          className={styles.comparisonOptionVote}
          onClick={(e) => {
            e.stopPropagation();
            handleVote(optionKey);
          }}
          disabled={voting}
          aria-pressed={isSelected}
        >
          <span className={styles.comparisonOptionTitle}>{title}</span>
          {hasVotes ? (
            <>
              <div className={styles.comparisonOptionProgressTrack} aria-hidden>
                <div
                  className={styles.comparisonOptionProgressFill}
                  style={{ width: `${percent || 0}%` }}
                />
              </div>
              <span className={styles.comparisonOptionMeta}>
                {votes || 0} vote{(votes || 0) === 1 ? "" : "s"} ·{" "}
                {percent || 0}%
              </span>
            </>
          ) : (
            <span className={styles.comparisonOptionCta}>Tap to vote</span>
          )}
        </button>
        {productId && (
          <Link
            to={`/product/${productId}`}
            className={styles.comparisonOptionProductLink}
            onClick={(e) => e.stopPropagation()}
            title={`Open ${capitalizeFirst(title)} product page`}
            aria-label={`Open ${capitalizeFirst(title)} product page`}
          >
            <FaExternalLinkAlt aria-hidden />
            View product
          </Link>
        )}
      </div>
    );
  };

  const handleLike = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location, action: "like post" } });
      return;
    }

    try {
      setLiking(true);
      const res = await api.post(`/posts/${post._id}/like`);
      setIsLiked(res.data.isLiked);
      setLikeCount(res.data.likeCount);
      onLikeUpdate?.(post._id, res.data.isLiked, res.data.likeCount);
    } catch (err) {
      console.error("Error liking post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to like post",
        type: "error",
      });
    } finally {
      setLiking(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location, action: "save post" } });
      return;
    }

    if (isSaved) {
      // Unsave immediately — no picker needed
      try {
        setSaving(true);
        await api.delete(`/posts/${post._id}/save`);
        setIsSaved(false);
        onSaveUpdate?.(post._id, false);
      } catch (err) {
        showToast({
          message: err.response?.data?.message || "Failed to unsave post",
          type: "error",
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Open collection picker
      setShowSaveModal(true);
    }
  };

  const handleSaveToCollection = async (collectionName) => {
    setShowSaveModal(false);
    try {
      setSaving(true);
      await api.post(`/posts/${post._id}/save`, { collectionName });
      setIsSaved(true);
      onSaveUpdate?.(post._id, true);
      // Refresh collection covers so the grid and save modal show the new image
      queryClient.invalidateQueries([
        "savedCollections",
        currentUser?.id || currentUser?._id,
      ]);
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Failed to save post",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePostFollow = async () => {
    if (!currentUser) {
      navigate("/login", {
        state: { from: location, action: "follow post notifications" },
      });
      return;
    }

    try {
      setFollowingPost(true);

      if (isFollowingPost) {
        await api.delete(`/posts/${post._id}/follow`);
        setIsFollowingPost(false);
      } else {
        await api.post(`/posts/${post._id}/follow`);
        setIsFollowingPost(true);
      }
    } catch (err) {
      console.error("Error updating post follow status:", err);
      showToast({
        message:
          err.response?.data?.message || "Failed to update post notifications",
        type: "error",
      });
    } finally {
      setFollowingPost(false);
      setShowMenu(false);
    }
  };

  const calculateNextComparisonStats = (currentStats, nextOption) => {
    const previousOption = currentStats?.userVote || null;
    let votesA = Number(currentStats?.votesA || 0);
    let votesB = Number(currentStats?.votesB || 0);

    if (previousOption === "A") votesA = Math.max(0, votesA - 1);
    if (previousOption === "B") votesB = Math.max(0, votesB - 1);
    if (nextOption === "A") votesA += 1;
    if (nextOption === "B") votesB += 1;

    const totalVotes = votesA + votesB;
    return {
      votesA,
      votesB,
      totalVotes,
      percentA: totalVotes ? Math.round((votesA / totalVotes) * 100) : 0,
      percentB: totalVotes ? Math.round((votesB / totalVotes) * 100) : 0,
      userVote: nextOption,
    };
  };

  const handleVote = async (option) => {
    if (!currentUser) {
      navigate("/login", {
        state: { from: location, action: "vote on comparison" },
      });
      return;
    }
    if (!isComparisonPost) return; // covers both "comparison" and "battle"

    const previousStats = comparisonStats;
    const optimisticStats = calculateNextComparisonStats(previousStats, option);
    setComparisonStats(optimisticStats);
    onVoteUpdate?.(post._id, optimisticStats);

    try {
      setVoting(true);
      const res = await api.post(`/posts/${post._id}/vote`, { option });
      const serverStats = res.data?.comparisonStats || optimisticStats;
      setComparisonStats(serverStats);
      onVoteUpdate?.(post._id, serverStats);
    } catch (err) {
      console.error("Error voting on comparison post:", err);
      setComparisonStats(previousStats);
      onVoteUpdate?.(post._id, previousStats);
      showToast({
        message: err.response?.data?.message || "Failed to submit vote",
        type: "error",
      });
    } finally {
      setVoting(false);
    }
  };

  const handleShare = (e) => {
    e?.stopPropagation?.();
    setShowShareModal(true);
  };

  const handleHidePost = async () => {
    if (!currentUser) {
      navigate("/login", { state: { from: location, action: "hide post" } });
      return;
    }

    try {
      await api.post(`/posts/${post._id}/hide`);
      setIsHidden(true);
      setShowMenu(false);
      onDelete?.(post._id);
      queryClient.invalidateQueries({ queryKey: ["feed"] });
    } catch (err) {
      console.error("Error hiding post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to hide post",
        type: "error",
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await api.delete(`/posts/${post._id}`);
      onDelete?.(post._id);
      queryClient.invalidateQueries({ queryKey: ["feed"] });
    } catch (err) {
      console.error("Error deleting post:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete post",
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const nextImage = useCallback(() => {
    if (productImageEntries.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % productImageEntries.length);
    }
  }, [productImageEntries.length]);

  const prevImage = useCallback(() => {
    if (productImageEntries.length > 1) {
      setCurrentImageIndex(
        (prev) =>
          (prev - 1 + productImageEntries.length) % productImageEntries.length,
      );
    }
  }, [productImageEntries.length]);

  const handleCardClick = useCallback(
    (e) => {
      // If card click is disabled, do nothing
      if (disableCardClick) {
        return;
      }

      // If the click was on a link, button, or svg (icon), don't navigate to the post page
      if (
        e.target.closest("button") ||
        e.target.closest("a") ||
        e.target.closest("svg")
      ) {
        return;
      }

      // Navigate to post details page
      const fromPath = `${location.pathname}${location.search}`;
      navigate(`/post/${post._id}`, { state: { from: fromPath } });
    },
    [disableCardClick, navigate, post._id, location.pathname, location.search],
  );

  const handleCardKeyDown = useCallback(
    (e) => {
      if (disableCardClick) return;
      const tag = e.target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target?.isContentEditable ||
        e.target.closest("button") ||
        e.target.closest("a") ||
        e.target.closest("svg") ||
        e.target.closest("input, textarea, select, [contenteditable='true']")
      ) {
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCardClick(e);
      }
    },
    [disableCardClick, handleCardClick],
  );

  const handleCommentClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (commentAction === "navigate") {
        // Navigate to post details
        if (onCommentNavigate) {
          onCommentNavigate();
        } else {
          const fromPath = `${location.pathname}${location.search}`;
          navigate(`/post/${post._id}`, { state: { from: fromPath } });
        }
        return;
      }
      if (commentAction === "modal") {
        // Open modal
        if (onCommentNavigate) {
          onCommentNavigate();
        }
        return;
      }
      setShowComments((prev) => !prev);
    },
    [
      commentAction,
      onCommentNavigate,
      location.pathname,
      location.search,
      navigate,
      post._id,
    ],
  );

  const applyLoadedImage = useCallback((img) => {
    if (!img) return;
    const imageUrl = img.currentSrc || img.src;
    if (!imageUrl) return;
    setIsImageLoaded(true);

    if (colorCache.current[imageUrl]) {
      setBackdropColor(colorCache.current[imageUrl]);
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const sampleSize = 50;
      canvas.width = sampleSize;
      canvas.height = sampleSize;

      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
      const pixels = imageData.data;

      let r = 0,
        g = 0,
        b = 0,
        count = 0;

      const borderThickness = 5;

      for (let y = 0; y < sampleSize; y++) {
        for (let x = 0; x < sampleSize; x++) {
          if (
            x < borderThickness ||
            x >= sampleSize - borderThickness ||
            y < borderThickness ||
            y >= sampleSize - borderThickness
          ) {
            const index = (y * sampleSize + x) * 4;
            const alpha = pixels[index + 3];
            if (alpha === 0) continue;

            r += pixels[index];
            g += pixels[index + 1];
            b += pixels[index + 2];
            count++;
          }
        }
      }

      if (count > 0) {
        const color = {
          r: Math.round(r / count),
          g: Math.round(g / count),
          b: Math.round(b / count),
        };
        colorCache.current[imageUrl] = color;
        setBackdropColor(color);
      }
    } catch (error) {
      console.debug("Color extraction failed", error);
    }
  }, []);

  const handleImageLoad = useCallback(
    (e) => {
      applyLoadedImage(e.target);
    },
    [applyLoadedImage],
  );

  useEffect(() => {
    if (!currentImageUrl) {
      setIsImageLoaded(false);
      return undefined;
    }

    setIsImageLoaded(false);

    const syncCachedImage = () => {
      const img = postImageRef.current;
      if (img?.complete && img.naturalWidth > 0) {
        applyLoadedImage(img);
      }
    };

    syncCachedImage();
    const frameId = requestAnimationFrame(syncCachedImage);

    return () => cancelAnimationFrame(frameId);
  }, [currentImageUrl, applyLoadedImage]);

  const comparisonHeadline = postTitle
    ? postTitle
    : isBattlePost
      ? "Vote for the winner"
      : null;

  const renderCaption = (wrapperClassName = styles.caption) => {
    if (!post.caption) return null;

    return (
      <div className={wrapperClassName}>
        <div className={styles.captionContent}>
          <span
            ref={captionRef}
            className={`${styles.captionText} ${
              isGridView && !isCaptionExpanded ? styles.captionTextClamped : ""
            }`}
          >
            {renderMentionText(post.caption)}
          </span>
          {isGridView && showReadMore && (
            <button
              type="button"
              className={styles.captionToggle}
              onClick={(e) => {
                e.stopPropagation();
                setIsCaptionExpanded(!isCaptionExpanded);
              }}
            >
              {isCaptionExpanded ? "Read less" : "Read more"}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isHidden) {
    return null;
  }

  return (
    <div
      id={`post-card-${post._id}`}
      className={`${styles.postCard} ${
        isGridView ? styles.postCardGrid : styles.postCardFull
      } ${className || ""}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={disableCardClick ? undefined : "link"}
      tabIndex={disableCardClick ? undefined : 0}
      aria-label={
        disableCardClick
          ? undefined
          : `View post by ${post.user?.firstName || "user"}`
      }
    >
      {/* Post Header */}
      <div className={styles.postHeader}>
        {post.user ? (
          <Link
            to={`/user/${post.user._id}`}
            className={styles.userInfo}
            onClick={(e) => e.stopPropagation()}
          >
            {post.user.avatar ? (
              <img
                src={getImageUrl(post.user.avatar)}
                alt={post.user?.firstName || "User"}
                className={styles.userAvatar}
              />
            ) : (
              <div className={styles.userAvatarPlaceholder}>
                {post.user?.firstName?.[0] || "U"}
              </div>
            )}
            <div className={styles.userDetails}>
              <div className={styles.userName}>
                {post.user?.firstName || ""} {post.user?.lastName || ""}
                {post.user?.expert?.status === "approved" && (
                  <span className={styles.expertBadge} title="Verified Expert">
                    <FaStar size={8} /> Expert
                  </span>
                )}
              </div>
              {post.user.username && (
                <div className={styles.userUsername}>@{post.user.username}</div>
              )}
            </div>
          </Link>
        ) : (
          <div className={styles.userInfo}>
            <div className={styles.userAvatarPlaceholder}>U</div>
            <div className={styles.userDetails}>
              <div className={styles.userName}>[Deleted User]</div>
            </div>
          </div>
        )}
        <div className={styles.headerRight}>
          {currentProduct && !isBattlePost && (
            <FeedMatchFusedLine
              product={currentProduct}
              activeDecision={activeDecision}
              shopperCategoryKeys={shopperCategoryKeys}
              relatedClassName={styles.matchFusedLineRelated}
              className={styles.matchFusedLine}
            />
          )}
          {post.postType === "flex" && (
            <span className={styles.flexTypeBadge}>
              <FaFire size={11} /> Flex
            </span>
          )}
          {post.postType === "question" && (
            <span className={styles.questionTypeBadge}>
              <FaQuestionCircle size={11} /> Question
            </span>
          )}
          {post.postType === "battle" && (
            <span className={styles.battleTypeBadge}>
              <FaBolt size={11} /> Battle
            </span>
          )}
          {post.routedToMe && (
            <span
              className={styles.questionRoutingBadge}
              title="You're qualified to answer"
            >
              You can help
            </span>
          )}
          {post.contentRelevanceReason && (
            <span
              className={styles.relevanceContextBadge}
              title={post.contentRelevanceReason}
            >
              {post.contentRelevanceReason}
            </span>
          )}
          {post.questionRoutingReason && !post.routedToMe && (
            <span
              className={styles.questionRoutingBadge}
              title={post.questionRoutingReason}
            >
              {post.questionRoutingReason}
            </span>
          )}
          <div className={styles.timestamp}>
            {formatSocialTimestamp(post.createdAt)}
          </div>
          <div className={styles.postActions}>
            {isOwnPost && (
              <span
                className={styles.visibilityIcon}
                title={visibilityDisplay.title}
                aria-label={visibilityDisplay.title}
              >
                <VisibilityIcon size={13} />
              </span>
            )}
            <div className={styles.menuContainer} ref={menuRef}>
              <button
                type="button"
                className={styles.menuButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                title="More options"
              >
                <FaEllipsisV />
              </button>
              {showMenu && (
                <div className={styles.menuDropdown}>
                  {isOwnPost && (
                    <>
                      <button
                        type="button"
                        className={styles.menuItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          if (onEdit) {
                            onEdit();
                          } else {
                            setShowEditModal(true);
                          }
                        }}
                      >
                        <FaEdit /> Edit Post
                      </button>
                      <button
                        type="button"
                        className={styles.menuItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          handleDeleteClick();
                        }}
                        disabled={deleting}
                      >
                        <FaTimes /> Delete Post
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePostFollow();
                    }}
                    disabled={followingPost}
                  >
                    <FaBell />
                    {isFollowingPost ? "Unfollow Post" : "Follow Post"}
                  </button>
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      setShowReportModal(true);
                    }}
                  >
                    <FaFlag /> Report Post
                  </button>
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHidePost();
                    }}
                  >
                    <FaEyeSlash /> Hide Post
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Images — hidden for battle/comparison; arena cards show product images */}
      {!isComparisonPost && productImageEntries.length > 0 && (
        <div
          className={`${styles.imageContainer} ${productImageEntries.length > 1 ? styles.multipleImages : ""}`}
          style={{ backgroundColor: containerBg }}
        >
          {currentImageUrl && (
            <div
              className={styles.imageBackdrop}
              style={{
                backgroundColor: containerBg,
              }}
            />
          )}
          <img
            ref={postImageRef}
            src={currentImageUrl}
            alt={`Post by ${post.user?.firstName || "User"}`}
            className={`${styles.postImage} ${
              isImageLoaded ? styles.postImageLoaded : styles.postImageLoading
            }`}
            onLoad={handleImageLoad}
            crossOrigin="anonymous"
            loading="lazy"
            decoding="async"
          />
          {productImageEntries.length > 1 && (
            <>
              <button
                type="button"
                className={`${styles.imageNavButton} ${styles.imageNavLeft}`}
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                <FaChevronLeft size={20} />
              </button>
              <button
                type="button"
                className={`${styles.imageNavButton} ${styles.imageNavRight}`}
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                <FaChevronRight size={20} />
              </button>
              <div className={styles.imagePagination}>
                {Array.from(
                  { length: productImageEntries.length },
                  (_, index) => (
                    <span
                      key={index}
                      className={`${styles.paginationDot} ${
                        index === currentImageIndex
                          ? styles.paginationDotActive
                          : ""
                      }`}
                    />
                  ),
                )}
              </div>
            </>
          )}
        </div>
      )}

      {isComparisonPost ? (
        <div
          className={`${styles.battleUnifiedSection} ${
            isBattlePost
              ? styles.battleUnifiedSectionBattle
              : styles.battleUnifiedSectionPoll
          }`}
        >
          <div className={styles.comparisonHeader}>
            <div className={styles.comparisonLabelRow}>
              {isBattlePost && (
                <FaBolt className={styles.comparisonIcon} aria-hidden />
              )}
              <span className={styles.comparisonLabel}>
                {isBattlePost ? "Battle" : "Comparison Poll"}
                {comparisonHeadline ? ` — ${comparisonHeadline}` : ""}
              </span>
            </div>
            <span className={styles.comparisonTotalVotes}>
              {comparisonStats.totalVotes || 0} vote
              {(comparisonStats.totalVotes || 0) === 1 ? "" : "s"}
            </span>
          </div>
          <div className={styles.comparisonArena}>
            {renderComparisonOption("A")}
            <span className={styles.comparisonVs} aria-hidden>
              VS
            </span>
            {renderComparisonOption("B")}
          </div>
          {renderCaption(styles.battleCaption)}
        </div>
      ) : (
        <>
          {postTitle && (
            <div className={styles.postTitleRow}>
              <h3 className={styles.postTitle}>{postTitle}</h3>
            </div>
          )}

          {post.products && post.products.length > 0 && (
            <div className={styles.productsSection}>
              <div className={styles.productsList}>
                {products.map((product, index) => {
                  const productId = getProductId(product);
                  const productName =
                    typeof product === "string"
                      ? "Product"
                      : product?.name || "Product";
                  const label = capitalizeFirst(productName);

                  return (
                    <div
                      key={productId || index}
                      className={styles.productTagItem}
                    >
                      {productId && typeof product !== "string" ? (
                        <Link
                          to={`/product/${productId}`}
                          className={styles.productTag}
                          onClick={(e) => e.stopPropagation()}
                          title={`Open ${label} product page`}
                          aria-label={`Open ${label} product page`}
                        >
                          {label}
                        </Link>
                      ) : (
                        <span className={styles.productTagStatic}>{label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {renderCaption()}
        </>
      )}

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div
          className={`${styles.tags} ${isGridView ? styles.tagsTruncated : ""}`}
        >
          {post.tags.map((tag, idx) => (
            <span key={idx} className={styles.tag}>
              7{tag}
            </span>
          ))}
        </div>
      )}

      {/* Engagement */}
      <div className={styles.engagement}>
        <div className={styles.likeContainer}>
          <button
            type="button"
            className={`${styles.engagementButton} ${
              isLiked ? styles.liked : ""
            }`}
            aria-pressed={isLiked}
            onClick={(e) => {
              e.stopPropagation();
              handleLike();
            }}
            disabled={liking}
          >
            {isLiked ? <FaHeart /> : <FaRegHeart />}
            <span>{`${likeCount} ${likeCount === 1 ? "Like" : "Likes"}`}</span>
          </button>
        </div>
        <button
          type="button"
          className={styles.engagementButton}
          onClick={handleCommentClick}
        >
          <FaComment />
          <span>
            {`${commentCount} ${commentCount === 1 ? "Comment" : "Comments"}`}
          </span>
        </button>
        <button
          type="button"
          className={styles.engagementButton}
          onClick={handleShare}
          title="Share post"
        >
          <FaShare />
          <span>Share</span>
        </button>
        <button
          type="button"
          className={`${styles.engagementButton} ${
            isSaved ? styles.saved : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleSave();
          }}
          disabled={saving}
          title={isSaved ? "Remove from saved" : "Save post"}
        >
          {isSaved ? <FaBookmark /> : <FaRegBookmark />}
          <span>{isSaved ? "Saved" : "Save"}</span>
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div ref={commentsRef}>
          <PostComments
            postId={post._id}
            postOwnerId={post.user?._id}
            onCommentAdded={(newComment) => {
              // For optimistic updates, just increment the counter
              if (newComment?.optimistic) {
                updateCommentCount((prev) => Number(prev) + 1);
              } else {
                // For API success callbacks, increment the counter
                updateCommentCount((prev) => Number(prev) + 1);
              }
            }}
            onCommentDeleted={(deletedCount = 1) => {
              updateCommentCount((prev) =>
                Math.max(0, Number(prev) - Number(deletedCount)),
              );
            }}
            onCommentsLoaded={(totalCount) => {
              // Only sync the count on initial load to avoid interfering with optimistic updates
              // After comments are initially loaded, let optimistic updates handle the count
              if (!hasLoadedComments) {
                updateCommentCount(Number(totalCount));
                setHasLoadedComments(true);
              }
            }}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmModal
          message="Are you sure you want to delete this post?"
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Edit Post */}
      {showEditModal && !onEdit && (
        <EditPost
          post={post}
          onPostUpdated={(updatedPost) => {
            setShowEditModal(false);
            // Update local state if needed
            if (onPostUpdated) {
              onPostUpdated(updatedPost);
            } else {
              // Fallback: reload the page to show updated post
              window.location.reload();
            }
          }}
          onCancel={() => setShowEditModal(false)}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="post"
          contentId={post._id}
          onReported={() => {
            setShowReportModal(false);
          }}
        />
      )}

      {/* Likes Modal */}
      <LikesModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        postId={post._id}
      />

      {showSaveModal && (
        <SaveCollectionModal
          userId={currentUser?.id || currentUser?._id}
          onSave={handleSaveToCollection}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      <SharePostModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        post={post}
      />
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const areEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change
  // Note: commentCount is managed as local state, so we don't compare post.commentCount
  // to allow re-renders when local commentCount state changes
  return (
    prevProps.post._id === nextProps.post._id &&
    prevProps.post.caption === nextProps.post.caption &&
    prevProps.post.postType === nextProps.post.postType &&
    prevProps.post.isLiked === nextProps.post.isLiked &&
    prevProps.post.isFollowingPost === nextProps.post.isFollowingPost &&
    prevProps.post.likeCount === nextProps.post.likeCount &&
    prevProps.post.products === nextProps.post.products &&
    prevProps.post.comparisonStats?.votesA ===
      nextProps.post.comparisonStats?.votesA &&
    prevProps.post.comparisonStats?.votesB ===
      nextProps.post.comparisonStats?.votesB &&
    prevProps.post.comparisonStats?.userVote ===
      nextProps.post.comparisonStats?.userVote &&
    prevProps.isGridView === nextProps.isGridView &&
    prevProps.defaultShowComments === nextProps.defaultShowComments &&
    prevProps.disableCardClick === nextProps.disableCardClick &&
    prevProps.className === nextProps.className &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.activeDecision?.sessionId ===
      nextProps.activeDecision?.sessionId &&
    prevProps.activeDecision?.stage === nextProps.activeDecision?.stage &&
    prevProps.shopperCategoryKeys === nextProps.shopperCategoryKeys
  );
};

export default React.memo(PostCard, areEqual);
